import React, { useState, useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { CheckCircle2, Circle, Clock, Users, Briefcase, Settings, Rocket, Plus, Edit2, Trash2, Save, X } from 'lucide-react'

interface PhaseTask {
  id: string
  phase_id: string
  task_order: number
  task_description: string
  is_completed: boolean
  completed_at: string | null
  completed_by: string | null
}

interface Phase {
  id: string
  phase_number: number
  phase_name: string
  duration: string
  description: string
  tasks: PhaseTask[]
}

export const ScopePhases: React.FC = () => {
  const { user } = useAuth()
  const [phases, setPhases] = useState<Phase[]>([])
  const [loading, setLoading] = useState(true)
  const [editingTask, setEditingTask] = useState<string | null>(null)
  const [editingTaskText, setEditingTaskText] = useState('')
  const [addingTaskToPhase, setAddingTaskToPhase] = useState<string | null>(null)
  const [newTaskText, setNewTaskText] = useState('')

  useEffect(() => {
    fetchPhases()
  }, [])

  const fetchPhases = async () => {
    try {
      const { data: phasesData, error: phasesError } = await supabase
        .from('scope_phases')
        .select('*')
        .order('phase_number', { ascending: true })

      if (phasesError) throw phasesError

      const { data: tasksData, error: tasksError } = await supabase
        .from('scope_phase_tasks')
        .select('*')
        .order('task_order', { ascending: true })

      if (tasksError) throw tasksError

      const phasesWithTasks = phasesData?.map(phase => ({
        ...phase,
        tasks: tasksData?.filter(task => task.phase_id === phase.id) || []
      })) || []

      setPhases(phasesWithTasks)
    } catch (error) {
      console.error('Error fetching phases:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTaskToggle = async (taskId: string, isCompleted: boolean) => {
    try {
      const updateData = {
        is_completed: !isCompleted,
        updated_by: user?.id,
        ...(isCompleted
          ? { completed_at: null, completed_by: null }
          : { completed_at: new Date().toISOString(), completed_by: user?.id })
      }

      const { error } = await supabase
        .from('scope_phase_tasks')
        .update(updateData)
        .eq('id', taskId)

      if (error) throw error
      await fetchPhases()
    } catch (error) {
      console.error('Error updating task:', error)
    }
  }

  const handleEditTask = async (taskId: string) => {
    if (!editingTaskText.trim()) return

    try {
      const { error } = await supabase
        .from('scope_phase_tasks')
        .update({
          task_description: editingTaskText,
          updated_by: user?.id
        })
        .eq('id', taskId)

      if (error) throw error
      setEditingTask(null)
      setEditingTaskText('')
      await fetchPhases()
    } catch (error) {
      console.error('Error updating task:', error)
    }
  }

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return

    try {
      const { error } = await supabase
        .from('scope_phase_tasks')
        .delete()
        .eq('id', taskId)

      if (error) throw error
      await fetchPhases()
    } catch (error) {
      console.error('Error deleting task:', error)
    }
  }

  const handleAddTask = async (phaseId: string) => {
    if (!newTaskText.trim()) return

    try {
      // Get the current max task order for this phase
      const { data: existingTasks } = await supabase
        .from('scope_phase_tasks')
        .select('task_order')
        .eq('phase_id', phaseId)
        .order('task_order', { ascending: false })
        .limit(1)

      const nextOrder = existingTasks && existingTasks.length > 0
        ? existingTasks[0].task_order + 1
        : 1

      const { error } = await supabase
        .from('scope_phase_tasks')
        .insert({
          phase_id: phaseId,
          task_order: nextOrder,
          task_description: newTaskText,
          created_by: user?.id,
          updated_by: user?.id
        })

      if (error) throw error
      setAddingTaskToPhase(null)
      setNewTaskText('')
      await fetchPhases()
    } catch (error) {
      console.error('Error adding task:', error)
    }
  }

  const startEditingTask = (task: PhaseTask) => {
    setEditingTask(task.id)
    setEditingTaskText(task.task_description)
  }

  const cancelEditingTask = () => {
    setEditingTask(null)
    setEditingTaskText('')
  }

  const startAddingTask = (phaseId: string) => {
    setAddingTaskToPhase(phaseId)
    setNewTaskText('')
  }

  const cancelAddingTask = () => {
    setAddingTaskToPhase(null)
    setNewTaskText('')
  }

  const getPhaseIcon = (phaseNumber: number) => {
    switch (phaseNumber) {
      case 1:
        return <Settings style={{ height: '24px', width: '24px' }} />
      case 2:
        return <Briefcase style={{ height: '24px', width: '24px' }} />
      case 3:
        return <Users style={{ height: '24px', width: '24px' }} />
      case 4:
        return <Rocket style={{ height: '24px', width: '24px' }} />
      default:
        return <Clock style={{ height: '24px', width: '24px' }} />
    }
  }

  const getPhaseColor = (phaseNumber: number) => {
    switch (phaseNumber) {
      case 1:
        return '#3b82f6'
      case 2:
        return '#8b5cf6'
      case 3:
        return '#f59e0b'
      case 4:
        return '#10b981'
      default:
        return '#6b7280'
    }
  }

  const calculateProgress = (tasks: PhaseTask[]) => {
    if (tasks.length === 0) return 0
    const completed = tasks.filter(t => t.is_completed).length
    return Math.round((completed / tasks.length) * 100)
  }

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <div style={{ color: '#6b7280' }}>Loading project phases...</div>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#111827',
          marginBottom: '8px',
          fontFamily: 'Lexend Deca, sans-serif'
        }}>
          Project Implementation Roadmap
        </h2>
        <p style={{
          color: '#6b7280',
          fontSize: '14px',
          fontFamily: 'Lexend Deca, sans-serif'
        }}>
          Track progress across all implementation phases
        </p>
      </div>

      <div style={{ display: 'grid', gap: '20px' }}>
        {phases.map((phase, index) => {
          const progress = calculateProgress(phase.tasks)
          const phaseColor = getPhaseColor(phase.phase_number)

          return (
            <div
              key={phase.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{
                background: `linear-gradient(135deg, ${phaseColor}15 0%, ${phaseColor}05 100%)`,
                borderBottom: '1px solid #e5e7eb',
                padding: '20px 24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: phaseColor,
                      color: 'white'
                    }}>
                      {getPhaseIcon(phase.phase_number)}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#111827',
                          fontFamily: 'Lexend Deca, sans-serif'
                        }}>
                          Phase {phase.phase_number}: {phase.phase_name}
                        </h3>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: phaseColor + '20',
                          color: phaseColor,
                          fontFamily: 'Lexend Deca, sans-serif'
                        }}>
                          {phase.duration}
                        </span>
                      </div>
                      <p style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        marginTop: '4px',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}>
                        {phase.description}
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: phaseColor,
                      fontFamily: 'Lexend Deca, sans-serif'
                    }}>
                      {progress}%
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      fontFamily: 'Lexend Deca, sans-serif'
                    }}>
                      {phase.tasks.filter(t => t.is_completed).length}/{phase.tasks.length} tasks
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ padding: '20px 24px' }}>
                <div style={{
                  height: '8px',
                  background: '#f3f4f6',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    width: `${progress}%`,
                    height: '100%',
                    background: phaseColor,
                    transition: 'width 0.3s ease'
                  }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {phase.tasks.map((task) => (
                    <div
                      key={task.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px',
                        background: task.is_completed ? '#f0fdf4' : '#f9fafb',
                        borderRadius: '8px',
                        border: `1px solid ${task.is_completed ? '#bbf7d0' : '#e5e7eb'}`,
                        transition: 'all 0.2s'
                      }}
                    >
                      <button
                        onClick={() => handleTaskToggle(task.id, task.is_completed)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {task.is_completed ? (
                          <CheckCircle2 style={{
                            height: '20px',
                            width: '20px',
                            color: '#10b981'
                          }} />
                        ) : (
                          <Circle style={{
                            height: '20px',
                            width: '20px',
                            color: '#d1d5db'
                          }} />
                        )}
                      </button>
                      <div style={{ flex: 1 }}>
                        {editingTask === task.id ? (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={editingTaskText}
                              onChange={(e) => setEditingTaskText(e.target.value)}
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontFamily: 'Lexend Deca, sans-serif'
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleEditTask(task.id)
                                } else if (e.key === 'Escape') {
                                  cancelEditingTask()
                                }
                              }}
                              autoFocus
                            />
                            <button
                              onClick={() => handleEditTask(task.id)}
                              style={{
                                padding: '4px',
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Save style={{ height: '14px', width: '14px' }} />
                            </button>
                            <button
                              onClick={cancelEditingTask}
                              style={{
                                padding: '4px',
                                background: '#6b7280',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <X style={{ height: '14px', width: '14px' }} />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{
                              fontSize: '14px',
                              color: task.is_completed ? '#6b7280' : '#111827',
                              fontFamily: 'Lexend Deca, sans-serif',
                              textDecoration: task.is_completed ? 'line-through' : 'none'
                            }}>
                              {task.task_description}
                            </span>
                            <div style={{ display: 'flex', gap: '4px', opacity: 0.6 }}
                                 onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                 onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}>
                              <button
                                onClick={() => startEditingTask(task)}
                                style={{
                                  padding: '4px',
                                  background: '#eff6ff',
                                  color: '#2563eb',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                title="Edit task"
                              >
                                <Edit2 style={{ height: '12px', width: '12px' }} />
                              </button>
                              <button
                                onClick={() => handleDeleteTask(task.id)}
                                style={{
                                  padding: '4px',
                                  background: '#fef2f2',
                                  color: '#dc2626',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                title="Delete task"
                              >
                                <Trash2 style={{ height: '12px', width: '12px' }} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Add new task */}
                  {addingTaskToPhase === phase.id ? (
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      padding: '12px',
                      background: '#f0f9ff',
                      borderRadius: '8px',
                      border: '1px solid #bfdbfe'
                    }}>
                      <div style={{ width: '20px' }} /> {/* Spacer for alignment */}
                      <div style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={newTaskText}
                          onChange={(e) => setNewTaskText(e.target.value)}
                          placeholder="Enter new task description..."
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontFamily: 'Lexend Deca, sans-serif'
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleAddTask(phase.id)
                            } else if (e.key === 'Escape') {
                              cancelAddingTask()
                            }
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleAddTask(phase.id)}
                          style={{
                            padding: '4px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Save style={{ height: '14px', width: '14px' }} />
                        </button>
                        <button
                          onClick={cancelAddingTask}
                          style={{
                            padding: '4px',
                            background: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <X style={{ height: '14px', width: '14px' }} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => startAddingTask(phase.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: '1px dashed #d1d5db',
                        borderRadius: '6px',
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontFamily: 'Lexend Deca, sans-serif',
                        width: '100%',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#9ca3af'
                        e.currentTarget.style.color = '#374151'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db'
                        e.currentTarget.style.color = '#6b7280'
                      }}
                    >
                      <Plus style={{ height: '14px', width: '14px' }} />
                      Add new task
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}